import IParamTypeDescriptor, { TParamVisibility } from "../database/interfaces/paramScript/IParamTypeDescriptor";
import TParameters from "./TParameters";

/** A descriptor `visibleWhen` feltétele (lásd IParamTypeDescriptor). */
export type TVisibleWhen = NonNullable<IParamTypeDescriptor["visibleWhen"]>;

/**
 * ADAT-VEZÉRELT feltétel kiértékelése: teljesül-e a paraméter `visibleWhen`-je az AKTUÁLIS
 * paraméter-állapotban. Hiányzó feltétel → true (a paraméter feltétel nélkül él).
 *
 * KÉT fogyasztója van, és ez SZÁNDÉKOSAN ugyanaz a függvény mindkettőnél:
 *   1) PropertyContainerCard — a kártya renderelése (a user ne lássa a nem releváns mezőt),
 *   2) ParametersProcessor.generateStyleScript — a Unity-KIMENET: a nem teljesülő feltételű paramot
 *      NEM küldjük ki. Erre azért van szükség, mert ilyenkor a hivatkozott elem a Unity-hierarchiában
 *      NEM LÉTEZIK, és a rá mutató stylescript-entry hibát dob. Konkrét eset: a konyhai alsószekrény
 *      keresztmerevítő-dekorja (Path '*.CabinetFrame.Railing.*') csak Single/Double railingnél
 *      értelmes; kikapcsolt merevítésnél (RailingType "Fill") nincs Railing elem, és a Unity
 *      "No matching element found for StyleElement: Path - *.CabinetFrame.Railing.*" /
 *      "Could not apply style StyleElement: ParameterName: Decor" hibát ír.
 *
 * A két hely azért osztozik egy implementáción, hogy a UI és a kimenet SOSE csúszhasson el egymástól
 * (ami látszó mező, az megy ki, és fordítva).
 */
export const isParamVisible = (parameters: TParameters, visibleWhen?: TVisibleWhen): boolean => {
    if (!visibleWhen) return true;

    // Feltétel-LISTA esetén MIND teljesülnie kell (ÉS). Így a láncolt függések is leírhatók adatból —
    // pl. "a polc áthelyezhető" ÉS "a polctartó több furat" —, anélkül hogy a feltételek kiértékelése
    // két különböző helyre kerülne.
    const holds = (condition: TParamVisibility): boolean => {
        const raw = parameters?.[condition.paramName]?.[condition.property];
        const value = condition.field && raw && typeof raw === "object" ? raw[condition.field] : raw;
        const actual = String(value ?? "");

        // `in` = megengedett értékek, `notIn` = kizárt értékek; ha mindkettő meg van adva, mindkettőnek
        // teljesülnie kell. A `notIn` azért kell, mert a tipikus feltétel a KIZÁRÁS: "mindenre érvényes,
        // KIVÉVE ha nincs front" / "KIVÉVE ha nulla polc". Ezt `in`-nel csak felsorolással lehetne leírni,
        // amiből egy új opció némán kimaradna — pont az a törékeny whitelist, amit kerülünk.
        // Nem új fogalom: az `effects.when` `in`/`notIn` párosát hozzuk át ide.
        if (condition.in && !condition.in.includes(actual)) return false;
        if (condition.notIn && condition.notIn.includes(actual)) return false;
        return true;
    };

    return Array.isArray(visibleWhen) ? visibleWhen.every(holds) : holds(visibleWhen);
};

export default isParamVisible;
